// src/features/authn/UriListEditor.tsx
// Repeatable single-value URI list editor (add/remove rows) for OAuth2 client
// redirect URIs — same row idiom as features/workflow/KeyValueEditor.tsx,
// adapted for a plain string list. Controlled: parent owns the array; empty
// rows are kept while editing and filtered out by the caller on submit
// (same semantics as the old newline-split textarea).

import { Button } from '@react/components';

export interface UriListEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  emptyLabel: string;
  placeholder: string;
  removeLabel: string;
  idPrefix?: string;
}

export function UriListEditor({ value, onChange, addLabel, emptyLabel, placeholder, removeLabel, idPrefix = 'uri-row' }: UriListEditorProps) {
  const update = (i: number, next: string) => {
    onChange(value.map((v, idx) => (idx === i ? next : v)));
  };

  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, '']);

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-sm text-muted-foreground">{emptyLabel}</p>}
      {value.map((uri, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            id={`${idPrefix}-${i}`}
            type="text"
            value={uri}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            autoCapitalize="off"
            aria-label={placeholder}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button type="button" variant="ghost" size="sm" aria-label={removeLabel} onClick={() => remove(i)} className="shrink-0">
            <svg className="h-4 w-4 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" onClick={add}>
        {addLabel}
      </Button>
    </div>
  );
}

export default UriListEditor;
